import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import { Container } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import useForm from '../../hooks/use-form';
import { alert, success } from '../../redux/ducks/notification';
import { createUser } from '../../services/api/user-api-service';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  form: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

export default function CustomerCreate({ history }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const loading = useSelector(state => state.request.loading);
  const dispatch = useDispatch();

  const stateSchema = {
    name: { value: '', error: '' },
    lastName: { value: '', error: '' },
    address: { value: '', error: '' },
    email: { value: '', error: '' },
    phone: { value: '', error: '' },
  };

  const stateValidatorSchema = {
    name: {
      required: true,
      validator: {
        func: value => /^([A-Za-zñÑáéíóúÁÉÍÓÚ\s]{2,20})$/.test(value),
        error: t('customer.form.validation.name'),
      },
    },
    lastName: {
      required: false,
      validator: {
        func: value => /^$|^([A-Za-zñÑáéíóúÁÉÍÓÚ\s]{2,20})$/.test(value),
        error: t('customer.form.validation.last_name'),
      },
    },
    address: {
      required: false,
      validator: {
        func: value => /^$|^[\S\s]{2,200}$/.test(value),
        error: t('customer.form.validation.address'),
      },
    },
    email: {
      required: false,
      validator: {
        func: value => /^$|^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value),
        error: t('customer.form.validation.email'),
      },
    },
    phone: {
      required: true,
      validator: {
        func: value => /^$|^.{9,50}$/.test(value),
        error: t('customer.form.validation.phone'),
      },
    },
  };

  const onSubmitForm = async () => {
    try {
      await createUser({
        name: name,
        lastName: lastName ? lastName : null,
        address: address ? address : null,
        email: email ? email : null,
        phone: phone,
        roles: 'ROLE_CUSTOMER',
      });
      dispatch(success(t('customer.create.success')));
      history.push('/dashboard/customers');
    } catch (e) {
      if (409 === e.response.status) {
        dispatch(alert(t('errors.customer.already_registered')));

        return;
      }
      dispatch(alert(t('errors.common.server')));
    }
  }

  const {
    dirty,
    values,
    errors,
    handleOnChange,
    handleOnSubmit,
    disable,
  } = useForm(stateSchema, stateValidatorSchema, onSubmitForm);

  const { name, lastName, address, email, phone } = values;

  return (
    <div className={classes.root}>
      <Container maxWidth={'md'}>
        <Paper>
          <Toolbar>
            <Grid container>
              <Grid item md={11} xs={11}>
                <Typography variant="h5">
                  {t('customer.toolbar.new')}
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
          <div className={classes.paper}>
            <form
              className={classes.form}
              onSubmit={handleOnSubmit}
              noValidate
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="name"
                    label={t('customer.form.fields.name')}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={name}
                    error={errors.name.length > 0 && dirty.name}
                    helperText={errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="lastName"
                    label={t('customer.form.fields.last_name')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={lastName}
                    error={errors.lastName.length > 0 && dirty.lastName}
                    helperText={errors.lastName}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="address"
                    label={t('customer.form.fields.address')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={address}
                    error={errors.address.length > 0 && dirty.address}
                    helperText={errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="email"
                    label={t('customer.form.fields.email')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={email}
                    error={errors.email.length > 0 && dirty.email}
                    helperText={errors.email}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phone"
                    required
                    label={t('customer.form.fields.phone')}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={phone}
                    error={errors.phone.length > 0 && dirty.phone}
                    helperText={errors.phone}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={disable || loading}
              >
                {t('customer.form.fields.button.save')}
              </Button>
            </form>
          </div>
        </Paper>
      </Container>
    </div>
  );
}
